/*
 * Due diligence questionnaires and assessments (configuration, questions, answers, controls, discussions), shared by TPRM and UCRM.
 */
import { AiDueDiligenceAssessmentAnalysisMetadata, DueDiligenceAssessmentControlAssessmentAi, DueDiligenceReviewAi } from './ArtificialIntelligence';
import { FileToBeUploaded, FileUpdates, UploadedFile } from './Files';
import { Effectiveness } from './OperationalControls';
import { RiskRating } from './RiskRating';

export interface DueDiligenceAssessmentReviewHuman {
    comments?: string;
    effectiveness?: Effectiveness;
    files: UploadedFile[];
    residual_risk?: number;
}

export enum DueDiligenceQuestionType {
    DATE = 'Date',
    FILE_UPLOAD = 'File upload',
    FREEFORM_TEXT = 'Freeform text',
    MULTIPLE_SELECT = 'Multiple-select',
    SINGLE_SELECT = 'Single-select',
}

export interface DueDiligenceSelectableAnswerOption {
    is_unacceptable: boolean;
    text: string;
}

export interface DueDiligenceQuestionConfigurationDate {
    _type: DueDiligenceQuestionType.DATE;
    is_required: boolean;
    mapped_risk_ratings: number[];
    text: string;
}

export interface DueDiligenceQuestionConfigurationFileUpload {
    _type: DueDiligenceQuestionType.FILE_UPLOAD;
    is_required: boolean;
    mapped_risk_ratings: number[];
    text: string;
}

export interface DueDiligenceQuestionConfigurationFreeformText {
    _type: DueDiligenceQuestionType.FREEFORM_TEXT;
    is_required: boolean;
    mapped_risk_ratings: number[];
    text: string;
}

export interface DueDiligenceQuestionConfigurationMultipleSelect {
    _type: DueDiligenceQuestionType.MULTIPLE_SELECT;
    options: DueDiligenceSelectableAnswerOption[];
    is_required: boolean;
    mapped_risk_ratings: number[];
    text: string;
}

export interface DueDiligenceQuestionConfigurationSingleSelect {
    _type: DueDiligenceQuestionType.SINGLE_SELECT;
    options: DueDiligenceSelectableAnswerOption[];
    is_required: boolean;
    mapped_risk_ratings: number[];
    text: string;
}

export type DueDiligenceQuestionConfiguration = DueDiligenceQuestionConfigurationDate | DueDiligenceQuestionConfigurationFileUpload | DueDiligenceQuestionConfigurationFreeformText | DueDiligenceQuestionConfigurationMultipleSelect | DueDiligenceQuestionConfigurationSingleSelect;

export interface DueDiligenceControlConfiguration {
    name: string;
    text: string;
    questions: DueDiligenceQuestionConfiguration[];
}

export interface DueDiligenceControlGroupConfiguration {
    controls: DueDiligenceControlConfiguration[];
    description: string;
    name: string;
}

export interface DueDiligenceControlFrameworkConfiguration {
    control_groups: DueDiligenceControlGroupConfiguration[];
    name: string;
}

export interface DueDiligenceAssessmentConfiguration {
    control_frameworks: DueDiligenceControlFrameworkConfiguration[];
    default_delegate_ids: string[];
    default_owner_id: string;
    id: string;
    is_default: boolean;
    name: string;

    expected_days_to_complete_assessment?: number;
    expected_days_to_complete_questionnaire?: number;
}

export interface CreateDueDiligenceAssessmentConfigurationRequest {
    default_delegates_ids: string[];
    default_owner_id: string;
    is_default: boolean;
    name: string;
}

export interface UpdateDueDiligenceAssessmentConfigurationRequest {
    configuration: DueDiligenceAssessmentConfiguration;
}

export interface DueDiligenceAnswer {
    _type: DueDiligenceQuestionType;
    new_files: FileToBeUploaded[];
    selected_existing_file_ids: string[];
    question_id: string;

    comments?: string;
}

export interface DueDiligenceAnswerDate extends DueDiligenceAnswer {
    _type: DueDiligenceQuestionType.DATE;
    is_unable_to_answer: boolean;
    date?: string;
}

export interface DueDiligenceAnswerFileUpload extends DueDiligenceAnswer {
    _type: DueDiligenceQuestionType.FILE_UPLOAD;
    is_unable_to_answer: boolean;
}

export interface DueDiligenceAnswerFreeformText extends DueDiligenceAnswer {
    _type: DueDiligenceQuestionType.FREEFORM_TEXT;
}

export interface DueDiligenceAnswerMultipleSelect extends DueDiligenceAnswer {
    _type: DueDiligenceQuestionType.MULTIPLE_SELECT;
    selections: number[];
}

export interface DueDiligenceAnswerSingleSelect extends DueDiligenceAnswer {
    _type: DueDiligenceQuestionType.SINGLE_SELECT;
    selection?: number;
}

export interface BaseDueDiligenceQuestion {
    _type: DueDiligenceQuestionType;
    id: string;
    is_required: boolean;
    text: string;
    comments?: string;
    files: UploadedFile[];
    generated_issue_id?: string;
    last_updated_at?: string;
}

export interface DueDiligenceQuestionDate extends BaseDueDiligenceQuestion {
    _type: DueDiligenceQuestionType.DATE;
    /**
     * Set when the respondent (a third-party contact in TPRM, a SummIT user in UCRM) has explicitly acknowledged that they cannot answer the question, which counts as a response for the purpose of submitting the questionnaire. It is shown to the user in the form of a checkbox only when the question is required.
     * This exists because a required Date question would otherwise be impossible to satisfy when the respondent genuinely has no date to identify.
     * Mutually exclusive with `answer`.
     */
    is_unable_to_answer: boolean;
    answer?: string;
}

export interface DueDiligenceQuestionFileUpload extends BaseDueDiligenceQuestion {
    _type: DueDiligenceQuestionType.FILE_UPLOAD;
    /**
     * Set when the respondent (a third-party contact in TPRM, a SummIT user in UCRM) has explicitly acknowledged that they cannot answer the question, which counts as a response for the purpose of submitting the questionnaire. It is shown to the user in the form of a checkbox only when the question is required.
     * This exists because a required File Upload question would otherwise be impossible to satisfy when the respondent genuinely has no document to upload.
     * Mutually exclusive with `files`.
     */
    is_unable_to_answer: boolean;
}

export interface DueDiligenceQuestionFreeformText extends BaseDueDiligenceQuestion {
    _type: DueDiligenceQuestionType.FREEFORM_TEXT;
}

export interface DueDiligenceQuestionMultipleSelect extends BaseDueDiligenceQuestion {
    _type: DueDiligenceQuestionType.MULTIPLE_SELECT;
    answer: number[];
    options: DueDiligenceSelectableAnswerOption[];
}

export interface DueDiligenceQuestionSingleSelect extends BaseDueDiligenceQuestion {
    _type: DueDiligenceQuestionType.SINGLE_SELECT;
    answer?: number;
    options: DueDiligenceSelectableAnswerOption[];
}

export type DueDiligenceQuestion = DueDiligenceQuestionDate | DueDiligenceQuestionFileUpload | DueDiligenceQuestionFreeformText | DueDiligenceQuestionMultipleSelect | DueDiligenceQuestionSingleSelect;

export interface DueDiligenceAssessmentControlAssessmentHuman {
    comments?: string;
    effectiveness?: Effectiveness;
    files: UploadedFile[];
}

export interface DueDiligenceControl {
    control_assessment_human: DueDiligenceAssessmentControlAssessmentHuman;
    identifier: string;
    name: string;
    text: string;
    questions: DueDiligenceQuestion[];

    control_assessment_ai?: DueDiligenceAssessmentControlAssessmentAi;
    last_updated_at?: string;
}

export interface DueDiligenceControlGroup {
    controls: DueDiligenceControl[];
    description: string;
    identifier: string;
    name: string;
}

export interface DueDiligenceControlFramework {
    control_groups: DueDiligenceControlGroup[];
    identifier: string;
    name: string;
}

export enum DiscussionMessageType {
    CLIENT = 'Client',
    THIRD_PARTY = 'Third-party',
}

export interface DiscussionMessageClient {
    _type: DiscussionMessageType.CLIENT;
    text: string;
    timestamp: string;
    user_id: string;
}

export interface DiscussionMessageThirdParty {
    _type: DiscussionMessageType.THIRD_PARTY;
    text: string;
    timestamp: string;
    user_email_address: string;
}

export type DiscussionMessage = DiscussionMessageClient | DiscussionMessageThirdParty;

export interface Discussion {
    marked_as_read_timestamps_client: { [userId: string]: string };
    marked_as_read_timestamps_third_party: { [emailAddress: string]: string };
    messages: DiscussionMessage[];
}

/**
 * The members shared by TPRM and UCRM due diligence assessments. Extending interfaces declare `questionnaire_submitted_by`, since TPRM questionnaires are submitted by third-party contacts (identified by email address) and UCRM questionnaires are submitted by SummIT users (identified by user ID).
 * @param service_id The ID of the assessment subject (TPRM third-party service or UCRM use case). The TPRM-specific name is fixed by existing TPRM data, unless we decide to do a migration.
 * @param third_party_id The ID of the organization (TPRM third party or UCRM business). The TPRM-specific name is fixed by existing TPRM data, unless we decide to do a migration.
 */
export interface DueDiligenceAssessmentBase {
    assessment_review_human: DueDiligenceAssessmentReviewHuman;
    assessment_wide_files: UploadedFile[];
    control_frameworks: DueDiligenceControlFramework[];
    owner_id: string;
    delegate_ids: string[];
    discussion: Discussion;
    id: string;
    name: string;
    service_id: string;
    third_party_id: string;

    ai_analysis_metadata?: AiDueDiligenceAssessmentAnalysisMetadata;
    assessment_due_date?: string;
    assessment_review_ai?: DueDiligenceReviewAi;
    assessment_submitted_by?: string;
    assessment_submitted_timestamp?: string;
    questionnaire_due_date?: string;
    questionnaire_submitted_timestamp?: string;
}

/**
 * A summary of a TPRM or UCRM due diligence assessment.
 * @param service_id The ID of the assessment subject (TPRM third-party service or UCRM use case). The TPRM-specific name is fixed by existing TPRM data, unless we decide to do a migration.
 * @param third_party_id The ID of the organization (TPRM third party or UCRM business). The TPRM-specific name is fixed by existing TPRM data, unless we decide to do a migration.
 */
export interface DueDiligenceAssessmentSummary {
    delegate_ids: string[];
    id: string;
    name: string;
    owner_id: string;
    service_id: string;
    third_party_id: string;

    assessment_due_date?: string;
    assessment_submitted_timestamp?: string;
}

export interface DueDiligenceAssessmentControlAssessmentUpdates {
    control_identifier: string;
    comments?: string;
    effectiveness?: Effectiveness;
    new_files: FileToBeUploaded[];
    selected_existing_file_ids: string[];
}

export interface SaveDueDiligenceControlGroupRequest {
    updates: DueDiligenceAssessmentControlAssessmentUpdates[];
}

export interface UpdateAssessmentWideFilesRequest {
    new_files: FileToBeUploaded[];
    selected_existing_file_ids: string[];
}

export interface UpdateDueDiligenceAssessmentAssignmentsRequest {
    owner_id: string;
    delegate_ids: string[];
    assessment_due_date?: string;
    questionnaire_due_date?: string;
}

export interface AssessmentSelections {
    assessment_id: string;
    assessment_due_date?: string;
    delegate_ids: string[];
    owner_id: string;
    questionnaire_due_date?: string;
}

export interface SaveOrSubmitDueDiligenceAssessmentRequest {
    is_submit: boolean;
    file_updates: FileUpdates;

    comments?: string;
    overall_control_effectiveness?: Effectiveness;
    residual_risk?: RiskRating;
}

export interface AddDiscussionMessageRequest {
    /** If true, and the assessment has been submitted and/or the questionnaire has been marked as completed, undoes the assessment submission and/or the questionnaire completion. */
    reopen_questionnaire: boolean;
    text: string;
}

/** Answers to the questions within a single control group of a questionnaire. Used by third-party users in TPRM and by questionnaire respondents (SummIT users) in UCRM. */
export interface SaveQuestionnaireControlGroupRequest {
    answers: DueDiligenceAnswer[];
}

/** The parts of a control needed to display and answer its questions. */
export interface QuestionnaireControl {
    identifier: string;
    name: string;
    questions: DueDiligenceQuestion[];
    text: string;
}

export interface QuestionnaireControlGroup {
    controls: QuestionnaireControl[];
    description: string;
    identifier: string;
    name: string;
}

export interface QuestionnaireControlFramework {
    control_groups: QuestionnaireControlGroup[];
    identifier: string;
    name: string;
}

/** The parts of a questionnaire needed to display and answer its questions. Satisfied by both the TPRM `PublicQuestionnaire` (answered by third-party users) and `DueDiligenceAssessmentBase` (answered by SummIT users in UCRM). */
export interface QuestionnaireContent {
    control_frameworks: QuestionnaireControlFramework[];
}

/**
 * Determines whether a question has been responded to. This drives the "not answered" markers, the sidebar counts, and the gate on submitting a questionnaire.
 * Important: a Date or File upload question flagged as `is_unable_to_answer` is considered answered even though it has no date/file. Unlike the backend, which has more complex logic regarding AI analyses, there is no differentiation on the frontend between "is answered" and "has been responded to".
 */
export const isQuestionAnswered = (question: DueDiligenceQuestion): boolean => {
    switch (question._type) {
        case DueDiligenceQuestionType.DATE:
            return question.is_unable_to_answer || question.answer !== undefined;
        case DueDiligenceQuestionType.FILE_UPLOAD:
            return question.is_unable_to_answer || question.files.length > 0;
        case DueDiligenceQuestionType.FREEFORM_TEXT:
            return question.comments !== undefined && question.comments.trim() !== '';
        case DueDiligenceQuestionType.MULTIPLE_SELECT:
            return question.answer.length > 0;
        case DueDiligenceQuestionType.SINGLE_SELECT:
            return question.answer !== undefined;
    }
};

export const hasUnacceptableAnswer = (question: DueDiligenceQuestion): boolean => {
    switch (question._type) {
        case DueDiligenceQuestionType.DATE:
        case DueDiligenceQuestionType.FILE_UPLOAD:
        case DueDiligenceQuestionType.FREEFORM_TEXT:
            return false;
        case DueDiligenceQuestionType.MULTIPLE_SELECT:
            return question.answer.some((index) => question.options[index].is_unacceptable);
        case DueDiligenceQuestionType.SINGLE_SELECT:
            if (question.answer !== undefined) {
                return question.options[question.answer].is_unacceptable;
            } else {
                return false;
            }
    }
};

/** Counts the questions in an assessment configuration that apply to an assessment subject with the given inherent risk rating. */
export const countApplicableDueDiligenceQuestions = (assessment: DueDiligenceAssessmentConfiguration, inherentRiskRating: RiskRating): number => {
    return assessment.control_frameworks
        .flatMap((framework) => framework.control_groups)
        .flatMap((group) => group.controls)
        .flatMap((control) => control.questions)
        .filter((question) => question.mapped_risk_ratings.includes(inherentRiskRating)).length;
};
